/**
 * Canonical FIAS Plugin Theme Constants
 *
 * Single source of truth for the theme colors provided to plugins.
 * Matches the Fias monochromatic theme defined in client/src/index.css.
 *
 * Used by:
 *   - plugin-dev-harness (local development)
 *   - plugin-builder-preview (builder preview iframe)
 *   - PluginRenderer (production plugin rendering)
 *   - bridge routes (server-side fallback)
 *   - arche-sdk tests
 */

import type { FiasBrand, FiasTheme } from './types';

const SYSTEM_FONTS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MONO_FONTS = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';

const SHARED_SPACING = { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' };
const SHARED_FONTS = { body: SYSTEM_FONTS, heading: SYSTEM_FONTS, mono: MONO_FONTS };
const SHARED_COMPONENTS: FiasTheme['components'] = {
  borderRadius: '0.5rem',
  buttonRadius: '0.375rem',
  cardRadius: '0.5rem',
  inputRadius: '0.375rem',
  shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  borderWidth: '1px',
};

export const DARK_THEME: FiasTheme = {
  mode: 'dark',
  colors: {
    primary: '#ffffff',
    primaryText: '#0a0a0a',
    secondary: '#1f1f1f',
    accent: '#3b82f6',
    background: '#0a0a0a',
    surface: '#171717',
    card: '#141414',
    cardText: '#ffffff',
    text: '#ffffff',
    textSecondary: '#a6a6a6',
    muted: '#1e1e1e',
    mutedText: '#737373',
    border: '#2e2e2e',
    error: '#ef4444',
    warning: '#f59e0b',
    success: '#22c55e',
    info: '#3b82f6',
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS,
  components: SHARED_COMPONENTS,
};

export const LIGHT_THEME: FiasTheme = {
  mode: 'light',
  colors: {
    primary: '#171717',
    primaryText: '#ffffff',
    secondary: '#e5e5e5',
    accent: '#2563eb',
    background: '#ffffff',
    surface: '#fafafa',
    card: '#ffffff',
    cardText: '#0a0a0a',
    text: '#0a0a0a',
    textSecondary: '#737373',
    muted: '#f5f5f5',
    mutedText: '#a3a3a3',
    border: '#e5e5e5',
    error: '#dc2626',
    warning: '#d97706',
    success: '#16a34a',
    info: '#2563eb',
  },
  spacing: SHARED_SPACING,
  fonts: SHARED_FONTS,
  components: SHARED_COMPONENTS,
};

export function getDefaultTheme(mode: 'light' | 'dark'): FiasTheme {
  return mode === 'light' ? LIGHT_THEME : DARK_THEME;
}

const BRAND_HEX = /^#[0-9a-fA-F]{6}$/;
/** Well above the platform's stored-logo cap (64 KB data URL); refuses a value nobody should draw. */
const BRAND_LOGO_URL_MAX = 256 * 1024;
const BRAND_NAME_MAX = 200;

/**
 * A Company brand in the shape apps receive it (`FiasTheme.brand`), or
 * `undefined` when there is nothing to show. Used by every host — the
 * platform, the builder preview and the dev harness — and again by the SDK on
 * what arrives, so an app can rely on the shape whoever sent it: colours are
 * `#RRGGBB` or null, the logo is a `data:image/` or `https:` URL or null.
 */
export function normalizeFiasBrand(input: unknown): FiasBrand | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const color = (v: unknown) => (typeof v === 'string' && BRAND_HEX.test(v) ? v : null);
  const name =
    typeof raw.displayName === 'string' && raw.displayName.trim() !== ''
      ? raw.displayName.trim().slice(0, BRAND_NAME_MAX)
      : null;
  const logo =
    typeof raw.logoUrl === 'string' &&
    raw.logoUrl.length <= BRAND_LOGO_URL_MAX &&
    (raw.logoUrl.startsWith('data:image/') || raw.logoUrl.startsWith('https://'))
      ? raw.logoUrl
      : null;
  const brand: FiasBrand = {
    displayName: name,
    logoUrl: logo,
    primaryColor: color(raw.primaryColor),
    accentColor: color(raw.accentColor),
    headerTextColor: color(raw.headerTextColor),
  };
  return Object.values(brand).some((v) => v !== null) ? brand : undefined;
}
